import type { FC } from 'react';
import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import { useInstantSearch } from 'react-instantsearch-hooks-web';
import { CircularProgress, Button, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { useMapData } from '../../../map-data/use-map-data';
import type { LcpnTheme } from '../../../styles/theme';
import { Icon } from '../../Icon';
import { HeatMapSearch } from '../../HeatMapSearch';
import { HeatMapSearchResults } from '../../HeatMapSearch/HeatMapSearchResults';
import { HeatMapResultItem } from '../../HeatMapSearch/HeatMapResultItem';

import { PickerSelect } from './PickerSelect';
import { ConfirmedSummaryCard } from './ConfirmedSummaryCard';

interface PickerFormState {
  countryCode: string;
}

interface PickerProps {
  selectionNumber: 1 | 2;
  initialCountryCode: string;
  initialIsConfirmed?: boolean;
  excludedCountryCodes: string[];
  onFormChange: (formState: PickerFormState) => void;
  onRemove?: () => void;
  onConfirm?: (confirmed: boolean) => void;
}

const usePickerStyles = createUseStyles((theme: LcpnTheme) => ({
  pickerContainer: {
    background: theme.colors.white,
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: theme.spacing(56),
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(6),
  },
  picker: {
    background: theme.colors['blue-100'],
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer',
    padding: theme.spacing(8, 0),
    width: '100%',
  },
  pickerActionText: {
    paddingTop: theme.spacing(3),
    color: theme.colors.white,
    fontSize: theme.fontSizes.h4,
  },
  selectorHeader: {
    background: theme.colors['blue-100'],
    minHeight: theme.spacing(10),
    display: 'flex',
    alignItems: 'center',
    color: theme.colors.white,
    padding: theme.spacing(0, 3),
    fontSize: theme.fontSizes.p2,
    justifyContent: 'space-between',
  },
  selectorContainer: {
    padding: theme.spacing(3),
    position: 'relative',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  searchWrapper: {
    minHeight: theme.spacing(10),
  },
  dropDownsWrapper: {
    background: theme.colors['grey-100'],
    marginTop: theme.spacing(3),
    padding: theme.spacing(3),
    boxSizing: 'border-box',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    '& > div:not(:first-child)': {
      paddingTop: theme.spacing(3),
    },
  },
  clearButton: {
    alignSelf: 'flex-end',
  },
}));

const HeatMapSearchResultsList: FC<{
  onItemClick: (countryCode: string) => void;
}> = ({ onItemClick }) => {
  const { indexUiState, setIndexUiState } = useInstantSearch();

  return (
    <HeatMapSearchResults
      resultItemComponent={props => (
        <HeatMapResultItem
          {...props}
          onResultClick={props => {
            onItemClick(props);
            setIndexUiState({
              ...indexUiState,
              query: undefined,
            });
          }}
        />
      )}
    />
  );
};

export const HeatMapPicker: FC<PickerProps> = ({
  selectionNumber,
  initialCountryCode,
  initialIsConfirmed = false,
  excludedCountryCodes,
  onFormChange,
  onRemove,
  onConfirm,
}) => {
  const classes = usePickerStyles();
  const theme = useTheme<LcpnTheme>();
  const { t } = useTranslation();
  const { countryHeatMapMetaData } = useMapData();
  const [selectedCountryCode, setSelectedCountryCode] =
    useState(initialCountryCode);
  const [isConfirmed, setIsConfirmed] = useState(initialIsConfirmed);

  // Always start with form open
  const [isPickingStudy, setIsPickingStudy] = useState(true);

  // Update parent whenever form state changes
  const prevFormStateRef = useRef({
    countryCode: '',
  });

  useEffect(() => {
    const currentState = {
      countryCode: selectedCountryCode,
    };

    // Only call onFormChange if something actually changed
    if (prevFormStateRef.current.countryCode !== currentState.countryCode) {
      prevFormStateRef.current = currentState;
      onFormChange(currentState);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCountryCode]);

  const filteredCountryCodes = useMemo(() => {
    if (!countryHeatMapMetaData) return [];

    return Object.keys(countryHeatMapMetaData).filter(
      code => !excludedCountryCodes.includes(code),
    );
  }, [countryHeatMapMetaData, excludedCountryCodes]);

  useEffect(() => {
    // Notify parent of confirmation state changes
    if (onConfirm) {
      onConfirm(isConfirmed && !!selectedCountryCode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConfirmed, selectedCountryCode]);

  const handleConfirm = useCallback(() => {
    if (selectedCountryCode) {
      setIsConfirmed(true);
    }
  }, [selectedCountryCode]);

  const handleEdit = useCallback(() => {
    setIsConfirmed(false);
  }, []);

  const handleClearAll = useCallback(() => {
    setSelectedCountryCode('');
  }, []);

  const isFormValid = !!selectedCountryCode;

  if (!isPickingStudy) {
    return (
      <div className={classes.pickerContainer}>
        <button
          className={classes.picker}
          onClick={() => setIsPickingStudy(true)}
        >
          <Icon variant="Add" width={64} height={64} />
          <p className={classes.pickerActionText}>
            {t('comparisonPicker.heatMap.picker.addEntry')}
          </p>
        </button>
      </div>
    );
  }

  if (!countryHeatMapMetaData) {
    return (
      <div className={classes.pickerContainer}>
        <div className={classes.selectorHeader}>
          <p>
            {t('comparisonPicker.heatMap.picker.title', { selectionNumber })}
          </p>
        </div>
        <div className={classes.loadingContainer}>
          <CircularProgress size={60} disableShrink />
        </div>
      </div>
    );
  }

  // Render confirmed summary card
  if (isConfirmed && selectedCountryCode && countryHeatMapMetaData) {
    const country = countryHeatMapMetaData[selectedCountryCode];
    return (
      <ConfirmedSummaryCard
        code={selectedCountryCode}
        title={country.countryName}
        details={[]}
        headerTitle={t('comparisonPicker.heatMap.picker.title', {
          selectionNumber,
        })}
        onEdit={handleEdit}
        onRemove={onRemove}
        compact
      />
    );
  }

  // Render form mode
  return (
    <div className={classes.pickerContainer}>
      <div className={classes.selectorHeader}>
        <p>{t('comparisonPicker.heatMap.picker.title', { selectionNumber })}</p>
        {onRemove && (
          <Button
            variant="text"
            sx={{
              color: 'white',
              fontSize: theme.fontSizes.p3,
              fontFamily: 'Be Vietnam Pro',
              textTransform: 'none',
              textDecoration: 'underline',
            }}
            size="small"
            onClick={onRemove}
          >
            Remove
          </Button>
        )}
      </div>
      <div className={classes.selectorContainer}>
        <div className={classes.searchWrapper}>
          <HeatMapSearch
            placeholder={t('comparisonPicker.heatMap.picker.searchPlaceHolder')}
          >
            <HeatMapSearchResultsList
              onItemClick={countryCode => {
                setSelectedCountryCode(countryCode);
              }}
            />
          </HeatMapSearch>
        </div>
        <div className={classes.dropDownsWrapper}>
          <Stack spacing={2}>
            <Button
              disabled={!selectedCountryCode}
              variant="text"
              size="small"
              className={classes.clearButton}
              sx={{
                lineHeight: 1,
                fontSize: theme.fontSizes.p3,
                fontFamily: 'Be Vietnam Pro',
                textTransform: 'none',
                textDecoration: 'underline',
                color: theme.colors['blue-100'],
                padding: 0,
                minWidth: 'auto',
              }}
              onClick={handleClearAll}
            >
              {t('comparisonPicker.clearSelection')}
            </Button>

            <PickerSelect
              onUpdate={countryCode => {
                setSelectedCountryCode(countryCode);
              }}
              placeholder={t(
                'comparisonPicker.heatMap.picker.countryPlaceholder',
              )}
              selectedValue={selectedCountryCode}
              options={filteredCountryCodes.map(code => ({
                label: `${code} - ${countryHeatMapMetaData[code].countryName}`,
                value: code,
              }))}
            />
            <Button
              onClick={handleConfirm}
              disabled={!isFormValid}
              color={'secondary'}
            >
              Confirm
            </Button>
          </Stack>
        </div>
      </div>
    </div>
  );
};
